import { CalendarMonth, Delete, Edit } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    LinearProgress,
    Paper,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableSortLabel,
    Tooltip,
    Typography,
} from "@mui/material";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/Table/DataTable.tsx";
import SearchSection from "@/components/Table/SearchSection.tsx";
import { useQueryOptionsFactory } from "@/queries";
import { useDisableOwnerMutation } from "@/queries/owners.ts";
import type { ListUnit } from "@/queries/units.ts";

type SortField = "ownerName" | "rentalId";
type SortDirection = "asc" | "desc";

const Root = (): ReactNode => {
    const qof = useQueryOptionsFactory();
    const pageData: { data: ListUnit[] } = useSuspenseQuery(qof.units.list()).data;
    const [deleteUnit, setDeleteUnit] = useState<ListUnit[] | null>(null);
    const [isLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [sortField, setSortField] = useState<SortField>("ownerName");
    const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
    // @todo infinite scroll pagination
    const [, setPage] = useState(0);
    const [displayedData, setDisplayedData] = useState<ListUnit[]>([]);
    const navigate = useNavigate();
    const rentalId = useSearch({
        from: "/admin/units/",
        select: (search: { rentalId?: string }) => search.rentalId,
    });
    const [searchTerm, setSearchTerm] = useState(rentalId ?? "");

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortDirection("asc");
        }
        // Reset pagination when sorting changes
        setPage(0);
        setDisplayedData([]);
        setHasMore(true);
    };

    const getSortedAndFilteredData = useCallback(() => {
        const filteredData = pageData.data.filter((unit) => {
            const searchLower = searchTerm.toLowerCase();
            return (
                unit.owner.ownerName.toLowerCase().includes(searchLower) ||
                unit.owner.ownerFirstName.toLowerCase().includes(searchLower) ||
                unit.rentalId.toLowerCase().includes(searchLower)
            );
        });

        return filteredData.sort((a, b) => {
            const aValue: string | number =
                sortField === "rentalId" ? Number.parseInt(a[sortField]) : a.owner[sortField];
            const bValue: string | number =
                sortField === "rentalId" ? Number.parseInt(b[sortField]) : b.owner[sortField];

            if (aValue < bValue) {
                return sortDirection === "asc" ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortDirection === "asc" ? 1 : -1;
            }
            return 0;
        });
    }, [searchTerm, sortField, sortDirection, pageData.data]);

    const totalFilteredCount = getSortedAndFilteredData().length;
    return (
        <Paper>
            <PageHeader
                title="Viewing and Editing data for all Units and their Owners"
                instructions={[
                    "Sort by clicking on headings.",
                    'You can View and/or Edit All Owner and Unit Data for any Unit under "View and/or Edit Data."',
                ]}
            />

            <SearchSection
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                onClearSearch={() => setSearchTerm("")}
                placeholder="Search by owner name or unit number..."
                displayedCount={displayedData.length}
                totalCount={totalFilteredCount}
                totalAllCount={pageData.data.length}
            />

            <DataTable
                isLoading={isLoading}
                hasMore={hasMore}
                onLoadMore={() => console.log("load more data...")}
                isEmpty={displayedData.length === 0}
                emptyMessage={
                    searchTerm ? "No units found matching your search." : "No units available."
                }
                colSpan={4}
                maxHeight={{ xs: "70vh", sm: "600px" }}
                loadingMessage="Loading more units..."
                endMessage="No more units to load"
            >
                <TableHead>
                    <TableRow
                        sx={{
                            "& .MuiTableCell-head": {
                                backgroundColor: "#f8fafc",
                                borderBottom: "2px solid #e2e8f0",
                                fontWeight: 600,
                                fontSize: { xs: "0.8rem", sm: "0.95rem" },
                                py: { xs: 1, sm: 1.5 },
                                px: { xs: 1, sm: 2 },
                            },
                        }}
                    >
                        <TableCell
                            sx={{
                                width: { xs: "45%", sm: "40%" },
                                minWidth: { xs: "120px", sm: "auto" },
                            }}
                        >
                            <TableSortLabel
                                active={sortField === "ownerName"}
                                direction={sortField === "ownerName" ? sortDirection : "asc"}
                                onClick={() => handleSort("ownerName")}
                                sx={{
                                    color: "#1e293b !important",
                                    "&:hover": { color: "#28638c !important" },
                                    "& .MuiTableSortLabel-icon": {
                                        color: "#28638c !important",
                                    },
                                }}
                            >
                                Owner Name
                            </TableSortLabel>
                        </TableCell>

                        <TableCell
                            sx={{
                                width: { xs: "15%", sm: "15%" },
                                minWidth: { xs: "80px", sm: "auto" },
                            }}
                        >
                            <TableSortLabel
                                active={sortField === "rentalId"}
                                direction={sortField === "rentalId" ? sortDirection : "asc"}
                                onClick={() => handleSort("rentalId")}
                                sx={{
                                    color: "#1e293b !important",
                                    "&:hover": { color: "#28638c !important" },
                                    "& .MuiTableSortLabel-icon": {
                                        color: "#28638c !important",
                                    },
                                }}
                            >
                                <Box
                                    component="span"
                                    sx={{
                                        textDecoration: "underline",
                                        textDecorationColor: "#f59e0b",
                                    }}
                                >
                                    {window.innerWidth < 600 ? "Unit #" : "Unit Number"}
                                </Box>
                            </TableSortLabel>
                        </TableCell>

                        <TableCell
                            align="center"
                            sx={{
                                width: { xs: "15%", sm: "15%" },
                                minWidth: { xs: "60px", sm: "auto" },
                            }}
                        >
                            <Typography sx={{ fontSize: { xs: "0.7rem", sm: "0.95rem" } }}>
                                {window.innerWidth < 600 ? "Calendar" : "View Calendar"}
                            </Typography>
                        </TableCell>

                        <TableCell
                            align="center"
                            sx={{
                                width: { xs: "15%", sm: "15%" },
                                minWidth: { xs: "60px", sm: "auto" },
                            }}
                        >
                            <Typography sx={{ fontSize: { xs: "0.7rem", sm: "0.95rem" } }}>
                                {window.innerWidth < 600 ? "Edit" : "View &/or Edit Data"}
                            </Typography>
                        </TableCell>

                        <TableCell
                            align="center"
                            sx={{
                                width: { xs: "15%", sm: "15%" },
                                minWidth: { xs: "60px", sm: "auto" },
                            }}
                        >
                            <Typography sx={{ fontSize: { xs: "0.7rem", sm: "0.95rem" } }}>
                                {window.innerWidth < 600 ? "Delete" : "Disable Owner and Unit"}
                            </Typography>
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {getSortedAndFilteredData().map((unit, index) => (
                        <TableRow
                            key={unit.id}
                            sx={{
                                backgroundColor: index % 2 === 0 ? "#ffffff" : "#f8fafc",
                                "&:hover": {
                                    backgroundColor: "#e0f2fe",
                                    transition: "background-color 0.2s ease",
                                },
                                "& .MuiTableCell-body": {
                                    borderBottom: "1px solid #e2e8f0",
                                    py: { xs: 1, sm: 1.5 },
                                    px: { xs: 1, sm: 2 },
                                },
                            }}
                        >
                            <TableCell
                                sx={{
                                    fontSize: { xs: "0.85rem", sm: "0.95rem" },
                                    color: "#1e293b",
                                    fontWeight: 500,
                                    wordBreak: { xs: "break-word", sm: "normal" },
                                }}
                            >
                                {unit.owner.ownerName}, {unit.owner.ownerFirstName}
                            </TableCell>
                            <TableCell>{unit.rentalId}</TableCell>
                            <TableCell align="center">
                                <Tooltip title={`View calendar for Unit ${unit.rentalId}`}>
                                    <IconButton
                                        onClick={() =>
                                            navigate({
                                                to: `/admin/units/${unit.id}/calendar`,
                                            })
                                        }
                                        sx={{
                                            color: "#28638c",
                                            backgroundColor: "#e0f2fe",
                                            size: { xs: "small", sm: "medium" },
                                            "&:hover": {
                                                backgroundColor: "#b3e5fc",
                                                transform: "scale(1.05)",
                                            },
                                            transition: "all 0.2s ease",
                                            p: { xs: 0.5, sm: 1 },
                                        }}
                                    >
                                        <CalendarMonth
                                            sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem" } }}
                                        />
                                    </IconButton>
                                </Tooltip>
                            </TableCell>

                            <TableCell align="center">
                                <Tooltip title={`View and edit data for Unit ${unit.rentalId}`}>
                                    <IconButton
                                        onClick={() =>
                                            navigate({ to: `/admin/units/${unit.id}/edit` })
                                        }
                                        sx={{
                                            color: "#f59e0b",
                                            backgroundColor: "#fef3c7",
                                            size: { xs: "small", sm: "medium" },
                                            "&:hover": {
                                                backgroundColor: "#fde68a",
                                                transform: "scale(1.05)",
                                            },
                                            transition: "all 0.2s ease",
                                            p: { xs: 0.5, sm: 1 },
                                        }}
                                    >
                                        <Edit sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem" } }} />
                                    </IconButton>
                                </Tooltip>
                            </TableCell>
                            <TableCell align="center">
                                <Tooltip title="Disable Owner and Unit">
                                    <IconButton
                                        onClick={() => {
                                            const units = pageData.data.filter(
                                                (u) => u?.owner?.id === unit?.owner?.id,
                                            );
                                            setDeleteUnit(units);
                                        }}
                                        sx={{
                                            color: "#f59e0b",
                                            backgroundColor: "#fef3c7",
                                            size: { xs: "small", sm: "medium" },
                                            "&:hover": {
                                                backgroundColor: "#fde68a",
                                                transform: "scale(1.05)",
                                            },
                                            transition: "all 0.2s ease",
                                            p: { xs: 0.5, sm: 1 },
                                        }}
                                    >
                                        <Delete sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem" } }} />
                                    </IconButton>
                                </Tooltip>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </DataTable>
            {deleteUnit !== null && (
                <DeleteModal units={deleteUnit} closeModal={() => setDeleteUnit(null)} />
            )}
        </Paper>
    );
};

type DeleteModalProps = {
    units: ListUnit[];
    closeModal: () => void;
};
const DeleteModal = ({ units, closeModal }: DeleteModalProps) => {
    const deleteMutate = useDisableOwnerMutation();

    useEffect(() => {
        if (deleteMutate.isSuccess) {
            closeModal();
        }
    }, [deleteMutate.isSuccess, closeModal]);

    const owner = units[0].owner;
    return (
        <Dialog open={true} onClose={closeModal}>
            <Box>
                <DialogTitle>Disable Ower and ower units</DialogTitle>
                <DialogContent>
                    This will disable {owner.ownerName}, {owner.ownerFirstName} ({owner.email})
                    along with their units {units.map((u) => u.rentalId).join(", ")}
                </DialogContent>
                <DialogActions>
                    <Button autoFocus onClick={closeModal} color="secondary">
                        Cancel
                    </Button>
                    <LoadingButton
                        onClick={() => {
                            deleteMutate.mutate(owner.id);
                        }}
                        color="primary"
                        loading={deleteMutate.isPending}
                    >
                        Disable
                    </LoadingButton>
                </DialogActions>
            </Box>
        </Dialog>
    );
};

export const Route = createFileRoute("/admin/units/")({
    component: Root,
    pendingComponent: LinearProgress,
    loader: async ({ context }) => {
        await context.queryClient.ensureQueryData(context.qof.units.list());
    },
});
